const QUERY = '.site-accordion';

const PROPS = {
    default: null,
    maxHeight: null,
    toggleAll: false,
    onChange: null
}

/**
 * This feature is used to set accordion behavior
 * 
 * @class Accordion
 * @param {Object} [query]
 * @param {Object} [props]
 */

export default class Accordion {
    constructor(query = QUERY, props = PROPS) {
        const el = document.querySelector(query);

        if (!el) throw new Error('Accordion not found');

        const items = el.querySelectorAll('.site-accordion-item');
        if (!items || !items.length) throw new Error('Accordion items not found');

        this.el = el;
        this.items = [];

        [...items].forEach((item) => {
            this.items.push({
                el: item,
                content: item.querySelector('.site-accordion-content'),
            });
        });

        this.default = props.default || props.default === 0 ? props.default : PROPS.default;
        this.maxHeight = props.maxHeight || PROPS.maxHeight;
        this.toggleAll = props.toggleAll || PROPS.toggleAll;
        this.onChange = props.onChange || PROPS.onChange;

        this.init();
    }

    init() {
        if (this.default || this.default === 0) {
            if (this.default < 0 || this.default >= this.items.length) throw new Error('Default item not found');

            this.handleChange(this.default);
        }

        if (this.maxHeight && this.maxHeight <= 0) throw new Error('Unexpected maxHeight value');

        if (this.toggleAll && !typeof this.toggleAll === 'boolean') throw new Error('Unexpected toggleAll value');

        this.items.forEach((item, index) => {
            item.el.addEventListener('click', (event) => {
                event.stopPropagation();

                this.handleChange(index);
            });
        });

        this.onResize = this.onResize.bind(this);
        window.addEventListener('resize', this.onResize);
    }

    handleChange(id) {
        if (!this.toggleAll) {
            for (let i = 0; i < this.items.length; i++) {
                if (i !== id) this.handleClose(this.items[i].el, this.items[i].content);
            }
        }

        if (!this.items[id].el.classList.contains('active')) {
            this.handleOpen(this.items[id].el, this.items[id].content);
        } else {
            this.handleClose(this.items[id].el, this.items[id].content);
        }

        if (this.onChange) {
            this.onChange(id);
        }
    }

    handleOpen(el, content) {
        if (!content) throw new Error('Accordion item content not found');

        let height = 0;

        if (!this.maxHeight) {
            content.style.maxHeight = 'auto';
            height = content.scrollHeight;
            content.style.maxHeight = 0;

            setTimeout(() => content.style.maxHeight = `${height}px`);
        } else {
            height = this.maxHeight;
        }

        el.classList.add('active');
        content.style.maxHeight = `${height}px`;
    }

    handleClose(el, content) {
        if (!content) throw new Error('Accordion item content not found');

        el.classList.remove('active');
        content.style.maxHeight = 0;
    }

    onResize() {
        this.items.forEach(({ el, content }) => {
            if (!el.classList.contains('active')) return;

            if (this.maxHeight) {
                content.style.maxHeight = `${this.maxHeight}px`;
                return;
            }

            content.style.maxHeight = 'auto';
            const height = content.scrollHeight;
            content.style.maxHeight = `${height}px`;
        });
    }
}

/**
 * Usage example
 * 
 * import Accordion from 'components/accordion';
 * 
 * new Accordion('.site-accordion', {
 *    default: 0,
 *    toggleAll: true,
 *    maxHeight: 400,
 *    onChange: (index) => console.log(`Accordion item changed: ${index}`)
 * });
 * 
 */
